import React, { useEffect, useState, useCallback } from 'react';
import { Form } from 'react-bootstrap';

import EnergyTracerMultiLineChart from '../../components/EnergyTracerMultiLineChart';
import CheckResources from './CheckResources';
import {
  BarChart,
  ResponsiveContainer,
  YAxis,
  XAxis,
  Tooltip,
  Bar,
  Legend,
} from 'recharts';
import CHARTCOLORS from '../../helpers/chartColors';
import formatValue from '../../helpers/chartFormatter';
import consumptionUnits from '../../helpers/consumptionUnits';
import DashboardBaselineDateSelectContainer from './DashboardBaselineDateSelectContainer';
import {
  ghg_units,
  short_ghg_units,
  ghg_conversions,
} from '../../helpers/GHGUnits';
import GraphActionContainer from '../../components/chartElements/GraphActionContainer';
import ChartDownload from '../../components/chartElements/Actions/ChartDownload';
import ChartToggle from '../../components/chartElements/Actions/ChartToggle';
import UtilityBreakdown from './UtilityBreakdown'
import { SUB_TYPES } from '../../helpers/utility-types';

const row = {
  marginRight: '40px',
  paddingLeft: '1em',
  marginTop: '1em',
};

const styles = {
  inputStyle: {
    width: '200px',
    textAlign: 'center',
    marginRight: '10px',
  },
  unit_form: { display: 'inline-block', marginLeft: '1em' },
};

const unit_utilities = ['gas', 'propane', 'water'];
const ghg_utilities = ['electric', 'gas', 'propane'];

export default function UtilityTrends(props) {
  const [deferredProps, setDeferredProps] = useState({ ...props });
  useEffect(() => {
    if (props.open) setDeferredProps({ ...props });
  }, [props]);
  const {
    dashboardData,
    organization,
    endDate,
    dateOption,
    activeIds,
    activeKeys,
    activeGHGLicense,
    open,
  } = deferredProps;

  const [utilitySelected, setUtilitySelected] = useState('none');
  const [activeUtilities, setActiveUtilities] = useState([]);
  const [unitSelected, setUnitSelected] = useState('');

  const [activeGHGUnit, setActiveGHGUnit] = useState('lbs');
  const [GHGUnitFactor, setGHGUnitFactor] = useState(1);

  const [costData, setCostData] = useState([]);
  const [usageData, setUsageData] = useState([]);
  const [demandData, setDemandData] = useState([]);
  const [GHGData, setGHGData] = useState([]);
  const [convertedGHGData, setConvertedGHGData] = useState([]);
  const [costBarData, setCostBarData] = useState([]);
  const [costBarKeys, setCostBarKeys] = useState([]);
  const [subCostBarData, setSubCostBarData] = useState([]);
  const [subCostBarKeys, setSubCostBarKeys] = useState([]);
  const [usageBarData, setUsageBarData] = useState([]);
  const [usageBarKeys, setUsageBarKeys] = useState([]);

  const [costSummary, setCostSummary] = useState(true);
  const [usageSummary, setUsageSummary] = useState(true);
  const [demandSummary, setDemandSummary] = useState(true);
  const [GHGSummary, setGHGSummary] = useState(true);

  const [chartDownloadHeaders, setChartDownloadHeaders] = useState([]);
  const [chartDownloadKeys, setChartDownloadKeys] = useState([]);
  const [chartCostBarDownloadHeaders, setChartCostBarDownloadHeaders] = useState([]);
  const [chartCostBarDownloadKeys, setChartCostBarDownloadKeys] = useState([])
  const [chartSubCostBarDownloadHeaders, setChartSubCostBarDownloadHeaders] = useState([]);
  const [chartSubCostBarDownloadKeys, setChartSubCostBarDownloadKeys] = useState([]);
  const [chartUsageBarDownloadHeaders, setChartUsageBarDownloadHeaders] = useState([]);
  const [chartUsageBarDownloadKeys, setChartUsageBarDownloadKeys] = useState([]);

  const [baselineData, setBaselineData] = useState({});
  const [baseline2Data, setBaseline2Data] = useState({});


  useEffect(() => {
    setGHGUnitFactor(1 / ghg_conversions[activeGHGUnit]);
  }, [activeGHGUnit]);

  useEffect(() => {
    let altered_ghg_data = [];
    GHGData.forEach((month) => {
      let temp_month = {};
      Object.keys(month).forEach((key) => {
        if (['name', 'month'].includes(key)) {
          temp_month[key] = month[key];
        } else {
          temp_month[key] = month[key] * GHGUnitFactor;
        }
      });
      altered_ghg_data.push(temp_month);
    });
    setConvertedGHGData(altered_ghg_data);
  }, [GHGData, GHGUnitFactor]);

  const convertToChartForm = useCallback(
    (
      dashboardData,
      baselineData,
      baseline2Data,
      utilitySelected,
      unitSelected,
      chartType,
      activeIds,
      activeKeys
    ) => {
      const hasBaseline = Object.keys(baselineData).length > 0;
      const hasBaseline2 = Object.keys(baseline2Data).length > 0;
      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
        };
      });
      let factor = 1;
      if (
        unit_utilities.includes(utilitySelected) &&
        chartType === 'usage' &&
        Object.keys(dashboardData['conversions']).includes(utilitySelected) &&
        Object.keys(dashboardData['conversions'][utilitySelected]).includes(
          unitSelected
        )
      ) {
        factor = dashboardData['conversions'][utilitySelected][unitSelected];
      }

      data.forEach((entry, index) => {
        activeIds.forEach((id, index2) => {
          let key = activeKeys[index2];

          entry[key] =
            dashboardData['building_usage'][id] &&
              dashboardData['building_usage'][id][utilitySelected]
              ? dashboardData['building_usage'][id][utilitySelected][chartType][
              index
              ] * factor
              : 0;
          if (hasBaseline) {
            entry[key + ' Baseline'] =
              baselineData['building_usage'][id] &&
                baselineData['building_usage'][id][utilitySelected]
                ? baselineData['building_usage'][id][utilitySelected][
                chartType
                ][index] * factor
                : 0;
          }
          if (hasBaseline2) {
            entry[key + ' Baseline 2'] =
              baseline2Data['building_usage'][id] &&
                baseline2Data['building_usage'][id][utilitySelected]
                ? baseline2Data['building_usage'][id][utilitySelected][
                chartType
                ][index] * factor
                : 0;
          }
        });
      });
      return data;
    },
    []
  );

  const convertBarCosts = useCallback(
    (dashboardData, utilitySelected, activeIds) => {
      let cost_bar_keys = [];
      let local_ids = [...activeIds];

      let data = dashboardData['month_array'].map((month) => {
        return {
          name: month,
          month: month,
        };
      });
      data.forEach((entry, index) => {
        local_ids.forEach((id) => {
          let costs = {};
          if (
            dashboardData['building_usage'][id] &&
            dashboardData['building_usage'][id][utilitySelected] &&
            dashboardData['building_usage'][id][utilitySelected]['costs']
          ) {
            costs =
              dashboardData['building_usage'][id][utilitySelected]['costs'];
          }
          Object.keys(costs).forEach((cost) => {
            if (!cost_bar_keys.includes(cost) && costs[cost][index] !== 0) {
              cost_bar_keys.push(cost);
            }
            if (entry[cost]) {
              entry[cost] += costs[cost][index];
            } else {
              entry[cost] = costs[cost][index];
            }
          });
        });
      });
      setCostBarKeys(cost_bar_keys);
      return data;
    },
    []
  );

  const convertSubBarUsages = useCallback(
    (dashboardData, utilitySelected, activeIds) => {
      let usage_bar_keys = [];
      let local_ids = [...activeIds];

      if (Object.keys(SUB_TYPES).includes(utilitySelected)) {
        let data = dashboardData['month_array'].map((month) => {
          return {
            name: month,
            month: month,
          };
        });
        data.forEach((entry, index) => {
          local_ids.forEach((id) => {
            let sub_costs = {};
            if (
              dashboardData['building_usage'][id] &&
              dashboardData['building_usage'][id][utilitySelected] &&
              dashboardData['building_usage'][id][utilitySelected]['sub_costs']
            ) {
              sub_costs =
                dashboardData['building_usage'][id][utilitySelected]['sub_costs'];
            }
            Object.keys(sub_costs).forEach((usage) => {
              let cost_key = Object.keys(SUB_TYPES[utilitySelected]).includes(usage) ? SUB_TYPES[utilitySelected][usage] : usage
              if (!usage_bar_keys.includes(cost_key) && sub_costs[usage][index] !== 0) {
                usage_bar_keys.push(cost_key);
              }
              if (entry[cost_key]) {
                entry[cost_key] += sub_costs[usage][index];
              } else {
                entry[cost_key] = sub_costs[usage][index];
              }
            });
          });
        });
        setSubCostBarKeys(usage_bar_keys);
        return data;
      }
      else {
        return []
      }
    },
    []
  );

  const convertBarUsages = useCallback(
    (dashboardData, utilitySelected, activeIds) => {
      let usage_bar_keys = [];
      let local_ids = [...activeIds];

      if (Object.keys(SUB_TYPES).includes(utilitySelected)) {
        let data = dashboardData['month_array'].map((month) => {
          return {
            name: month,
            month: month,
          };
        });
        data.forEach((entry, index) => {
          local_ids.forEach((id) => {
            let sub_usages = {};
            if (
              dashboardData['building_usage'][id] &&
              dashboardData['building_usage'][id][utilitySelected] &&
              dashboardData['building_usage'][id][utilitySelected]['sub_usages']
            ) {
              sub_usages =
                dashboardData['building_usage'][id][utilitySelected]['sub_usages'];
            }
            Object.keys(sub_usages).forEach((usage) => {
              let usage_key = Object.keys(SUB_TYPES[utilitySelected]).includes(usage) ? SUB_TYPES[utilitySelected][usage] : usage
              if (!usage_bar_keys.includes(usage_key) && sub_usages[usage][index] !== 0) {
                usage_bar_keys.push(usage_key);
              }
              if (entry[usage_key]) {
                entry[usage_key] += sub_usages[usage][index];
              } else {
                entry[usage_key] = sub_usages[usage][index];
              }
            });
          });
        });
        setUsageBarKeys(usage_bar_keys);
        return data;
      }
      else {
        return []
      }
    },
    []
  );

  const setOrgData = useCallback(() => {
    if (Object.keys(dashboardData).length) {
      const utilities = [...Object.keys(dashboardData['organization_usage']), 'total']
      setActiveUtilities(utilities);
      const utility = utilities.includes(
        utilitySelected
      )
        ? utilitySelected
        : Object.keys(dashboardData['organization_usage'])[0];

      setUtilitySelected(utility);
      setCostData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'cost',
          activeIds,
          activeKeys
        )
      );
      setUsageData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'usage',
          activeIds,
          activeKeys
        )
      );
      setDemandData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'demand',
          activeIds,
          activeKeys
        )
      );

      setGHGData(
        convertToChartForm(
          dashboardData,
          baselineData,
          baseline2Data,
          utility,
          unitSelected,
          'ghg',
          activeIds,
          activeKeys
        )
      );
      setCostBarData(convertBarCosts(dashboardData, utility, activeIds));
      setSubCostBarData(convertSubBarUsages(dashboardData, utility, activeIds));
      setUsageBarData(convertBarUsages(dashboardData, utility, activeIds));
    } else {
      setActiveUtilities([]);
      setCostData([]);
      setUsageData([]);
      setDemandData([]);
      setGHGData([]);
      setConvertedGHGData([]);
    }
  }, [
    dashboardData,
    baselineData,
    baseline2Data,
    convertToChartForm,
    convertBarCosts,
    convertSubBarUsages,
    convertBarUsages,
    utilitySelected,
    activeIds,
    activeKeys,
    unitSelected,
  ]);

  useEffect(() => {
    if (unit_utilities.includes(utilitySelected)) {
      setUnitSelected(consumptionUnits[utilitySelected][0]);
    } else if (utilitySelected === 'electric') {
      setUnitSelected('kwh');
    } else {
      setUnitSelected('');
    }
  }, [utilitySelected]);

  useEffect(() => {
    setOrgData();
  }, [setOrgData]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    activeKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
      if (Object.keys(baselineData).length > 0) {
        headers.push(key + ' Baseline');
        keys.push(key + ' Baseline');
      }
      if (Object.keys(baseline2Data).length > 0) {
        headers.push(key + ' Baseline 2');
        keys.push(key + ' Baseline 2');
      }
    });
    setChartDownloadHeaders(headers);
    setChartDownloadKeys(keys);
  }, [activeKeys, baselineData, baseline2Data]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    costBarKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartCostBarDownloadHeaders(headers);
    setChartCostBarDownloadKeys(keys);
  }, [costBarKeys]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    subCostBarKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartSubCostBarDownloadHeaders(headers);
    setChartSubCostBarDownloadKeys(keys);
  }, [subCostBarKeys]);

  useEffect(() => {
    let headers = ['Date'];
    let keys = ['name'];
    usageBarKeys.forEach((key) => {
      headers.push(key);
      keys.push(key);
    });
    setChartUsageBarDownloadHeaders(headers);
    setChartUsageBarDownloadKeys(keys);
  }, [usageBarKeys]);

  useEffect(() => {
    if (Object.keys(baselineData).length === 0) {
      setBaseline2Data({})
    }
  }
    , [baselineData])


  return (
    <div>
      <CheckResources
        organization={organization}
        dashboardData={dashboardData}
      />
      <div>
        <div style={{ display: 'inline-block' }}>
          <Form inline>
            <Form.Group>
              <Form.Label style={{ display: 'block', marginRight: '5px' }}>
                Utility:
              </Form.Label>
              <Form.Control
                style={styles.inputStyle}
                as='select'
                size='sm'
                onChange={(e) => {
                  setUtilitySelected(e.target.value);
                }}
                value={utilitySelected}
                data-id='db_org_utility'
              >
                {activeUtilities.map((utility) => (
                  <option
                    key={`util-tab-${utility.toLowerCase()}`}
                    value={utility.toLowerCase()}
                  >
                    {utility.charAt(0).toUpperCase() +
                      utility.substr(1).toLowerCase()}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {unit_utilities.includes(utilitySelected) && (
              <Form.Group>
                <Form.Label style={{ display: 'block', marginRight: '5px' }}>
                  Unit:
                </Form.Label>
                <Form.Control
                  style={styles.inputStyle}
                  as='select'
                  size='sm'
                  onChange={(e) => {
                    setUnitSelected(e.target.value);
                  }}
                  value={unitSelected}
                  data-id='db_org_unit'
                >
                  {consumptionUnits[utilitySelected].map((unit) => (
                    <option key={`unit-tab-${unit}`} value={unit}>
                      {unit}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            )}
          </Form>
        </div>
      </div>
      <div style={{ display: 'inline-block', margin: '1em 0em' }}>
        <DashboardBaselineDateSelectContainer
          orgId={organization.id}
          endDate={endDate}
          dateOption={dateOption}
          setBaselineData={setBaselineData}
        />
      </div>
      {Object.keys(baselineData).length > 0 &&
        <div style={{ display: 'inline-block', margin: '1em 0em', marginLeft: '2em' }}>
          <DashboardBaselineDateSelectContainer
            orgId={organization.id}
            endDate={endDate}
            dateOption={dateOption}
            setBaselineData={setBaseline2Data}
            baselineIndex={2}
          />
        </div>}

      {utilitySelected === 'total' &&
        <UtilityBreakdown
          dashboardData={dashboardData}
          baselineData={baselineData}
          baseline2Data={baseline2Data}
          organization={organization}
          open={open}
          activeIds={activeIds}
          activeKeys={activeKeys}
          activeGHGLicense={activeGHGLicense}
        />}

      <div style={{ display: utilitySelected !== 'total' ? 'block' : 'none' }}>

        <h5>{'Total Cost ($)'} </h5>
        <div style={{ ...row, marginBottom: '4em' }}>
          <EnergyTracerMultiLineChart
            data={!costData ? [] : costData}
            unit={'$'}
            keys={activeKeys}
            baseline={Object.keys(baselineData).length > 0}
            baseline2={Object.keys(baseline2Data).length > 0}
            showSummary={costSummary}
          />
          <GraphActionContainer>
            <ChartToggle
              onClick={() => {
                setCostSummary((prev) => !prev);
              }}
              state={costSummary ? 1 : 0}
              title={'Toggle Selection Total'}
              variant={3}
            />
            <ChartDownload
              organization_id={organization.id}
              data={costData}
              headers={chartDownloadHeaders}
              keys={chartDownloadKeys}
              chart_type={'line'}
            />
          </GraphActionContainer>
        </div>
        {costBarKeys.length > 1 && (
          <>
            <br />
            <br></br> <h5>{'Cost Breakdown ($)'}</h5>
            <div style={{ ...row, marginBottom: '4em' }}>
              <ResponsiveContainer width={'100%'} height={300} debounce={300}>
                <BarChart data={costBarData}>
                  <YAxis
                    type='number'
                    tickFormatter={(tick) => {
                      return tick.toLocaleString();
                    }}
                  />
                  <XAxis type='category' dataKey='name' tick={false}>
                    {' '}
                  </XAxis>
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ bottom: '0px', position: 'relative' }}
                  />
                  {costBarKeys.map((cost, idx) => (
                    <Bar
                      dataKey={cost}
                      fill={CHARTCOLORS[idx]}
                      formatter={(value) => formatValue(value, '$')}
                      stackId='a'
                      key={cost + '_' + idx}
                    ></Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <GraphActionContainer customStyle={{ marginTop: '1em' }}>
                <ChartDownload
                  organization_id={organization.id}
                  data={costBarData}
                  headers={chartCostBarDownloadHeaders}
                  keys={chartCostBarDownloadKeys}
                  chart_type={'bar'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
        {subCostBarKeys.length > 1 && (
          <>
            <br />
            <br></br> <h5>{'Sub-Type Cost Breakdown ($)'}</h5>
            <div style={{ ...row, marginBottom: '4em' }}>
              <ResponsiveContainer width={'100%'} height={300} debounce={300}>
                <BarChart data={subCostBarData}>
                  <YAxis
                    type='number'
                    tickFormatter={(tick) => {
                      return tick.toLocaleString();
                    }}
                  />
                  <XAxis type='category' dataKey='name' tick={false}>
                    {' '}
                  </XAxis>
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ bottom: '0px', position: 'relative' }}
                  />
                  {subCostBarKeys.map((cost, idx) => (
                    <Bar
                      dataKey={cost}
                      fill={CHARTCOLORS[idx]}
                      formatter={(value) => formatValue(value, '$')}
                      stackId='a'
                      key={cost + '_' + idx}
                    ></Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <GraphActionContainer customStyle={{ marginTop: '1em' }}>
                <ChartDownload
                  organization_id={organization.id}
                  data={subCostBarData}
                  headers={chartSubCostBarDownloadHeaders}
                  keys={chartSubCostBarDownloadKeys}
                  chart_type={'bar'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
        <br />
        <br></br>
        <h5>{'Total Consumption (' + unitSelected + ')'} </h5>
        <div style={{ ...row, marginBottom: '6em' }}>
          <EnergyTracerMultiLineChart
            data={!usageData ? [] : usageData}
            unit={unitSelected}
            keys={activeKeys}
            baseline={Object.keys(baselineData).length > 0}
            baseline2={Object.keys(baseline2Data).length > 0}
            showSummary={usageSummary}
          />
          <GraphActionContainer>
            <ChartToggle
              onClick={() => {
                setUsageSummary((prev) => !prev);
              }}
              state={usageSummary ? 1 : 0}
              title={'Toggle Selection Total'}
              variant={3}
            />
            <ChartDownload
              organization_id={organization.id}
              data={usageData}
              headers={chartDownloadHeaders}
              keys={chartDownloadKeys}
              chart_type={'line'}
            />
          </GraphActionContainer>
        </div>{' '}
        {usageBarKeys.length > 1 && (
          <>
            <br />
            <br></br> <h5>{'Consumption Breakdown (' + unitSelected + ')'}</h5>
            <div style={{ ...row, marginBottom: '4em' }}>
              <ResponsiveContainer width={'100%'} height={300} debounce={300}>
                <BarChart data={usageBarData}>
                  <YAxis
                    type='number'
                    tickFormatter={(tick) => {
                      return tick.toLocaleString();
                    }}
                  />
                  <XAxis type='category' dataKey='name' tick={false}>
                    {' '}
                  </XAxis>
                  <Tooltip />
                  <Legend
                    wrapperStyle={{ bottom: '0px', position: 'relative' }}
                  />
                  {usageBarKeys.map((usage, idx) => (
                    <Bar
                      dataKey={usage}
                      fill={CHARTCOLORS[idx]}
                      formatter={(value) => formatValue(value, unitSelected)}
                      stackId='a'
                      key={usage + '_' + idx}
                    ></Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
              <GraphActionContainer customStyle={{ marginTop: '1em' }}>
                <ChartDownload
                  organization_id={organization.id}
                  data={usageBarData}
                  headers={chartUsageBarDownloadHeaders}
                  keys={chartUsageBarDownloadKeys}
                  chart_type={'bar'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
        {utilitySelected === 'electric' && (
          <>
            <h5>{'Total Demand (kW)'}</h5>
            <div style={{ ...row, marginBottom: '6em' }}>
              <EnergyTracerMultiLineChart
                data={!demandData ? [] : demandData}
                unit={'kW'}
                keys={activeKeys}
                baseline={Object.keys(baselineData).length > 0}
                baseline2={Object.keys(baseline2Data).length > 0}
                showSummary={demandSummary}
              />
              <GraphActionContainer>
                <ChartToggle
                  onClick={() => {
                    setDemandSummary((prev) => !prev);
                  }}
                  state={demandSummary ? 1 : 0}
                  title={'Toggle Selection Total'}
                  variant={3}
                />
                <ChartDownload
                  organization_id={organization.id}
                  data={demandData}
                  headers={chartDownloadHeaders}
                  keys={chartDownloadKeys}
                  chart_type={'line'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
        {ghg_utilities.includes(utilitySelected) && activeGHGLicense && (
          <>
            <h5>
              {'Total Emissions'}

              <Form.Group controlId={'ghg-unit-select'} style={styles.unit_form}>
                <Form.Control
                  as='select'
                  onChange={(e) => setActiveGHGUnit(e.target.value)}
                  defaultValue={activeGHGUnit}
                >
                  {Object.keys(ghg_units).map((unit) => (
                    <option key={`unit-opt-${unit}`} value={unit}>
                      {ghg_units[unit]}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </h5>
            <div style={{ ...row, marginBottom: '6em' }}>
              <EnergyTracerMultiLineChart
                data={!convertedGHGData ? [] : convertedGHGData}
                unit={short_ghg_units[activeGHGUnit]}
                keys={activeKeys}
                baseline={Object.keys(baselineData).length > 0}
                baseline2={Object.keys(baseline2Data).length > 0}
                showSummary={GHGSummary}
              />
              <GraphActionContainer>
                <ChartToggle
                  onClick={() => {
                    setGHGSummary((prev) => !prev);
                  }}
                  state={GHGSummary ? 1 : 0}
                  title={'Toggle Selection Total'}
                  variant={3}
                />
                <ChartDownload
                  organization_id={organization.id}
                  data={convertedGHGData}
                  headers={chartDownloadHeaders}
                  keys={chartDownloadKeys}
                  chart_type={'line'}
                />
              </GraphActionContainer>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
